/**
 * Bigger/smaller text for the run-mode period sheet.
 *
 * Browser zoom is off app-wide (maximum-scale=1), so a drill that is only
 * words — description, steps, notes — couldn't be enlarged on the field.
 * Rather than magnify (which would need sideways panning), this RESIZES the
 * text so it re-wraps to the screen: pinch on the text, or use A−/A+.
 * One finger still scrolls. The size is remembered on this device only.
 *
 * Children size themselves in em, so everything inside scales together.
 * Diagrams are unaffected; they have their own full-screen zoom.
 */
import { TEXT_SCALE_KEY, TEXT_SCALE_STEP, clampTextScale, kvGet, kvSet } from '@coach/core';
import { useEffect, useRef, useState, type ReactNode } from 'react';

const BASE_PX = 15;

export function TextZoom({ children }: { children: ReactNode }) {
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void kvGet<number>(TEXT_SCALE_KEY).then((v) => {
      if (typeof v === 'number') setScale(clampTextScale(v));
    });
  }, []);
  const commit = (s: number) => {
    const c = clampTextScale(s);
    setScale(c);
    void kvSet(TEXT_SCALE_KEY, c);
  };

  // Touch events, non-passive, so a two-finger pinch can be claimed from the
  // page without stealing one-finger scrolling.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    let start: { dist: number; scale: number } | null = null;
    const dist = (t: TouchList) => Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY) || 1;
    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 2) start = { dist: dist(e.touches), scale: scaleRef.current };
    };
    const onMove = (e: TouchEvent) => {
      if (!start || e.touches.length !== 2) return;
      e.preventDefault();
      setScale(clampTextScale(start.scale * (dist(e.touches) / start.dist)));
    };
    const onEnd = (e: TouchEvent) => {
      if (start && e.touches.length < 2) {
        start = null;
        void kvSet(TEXT_SCALE_KEY, scaleRef.current);
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  return (
    <div>
      <div className="textzoom-bar">
        <button className="btn btn-sm" onClick={() => commit(scale / TEXT_SCALE_STEP)} aria-label="Smaller text">A−</button>
        <button className="btn btn-sm" onClick={() => commit(scale * TEXT_SCALE_STEP)} aria-label="Bigger text" style={{ fontSize: '1.1em' }}>A+</button>
        {scale !== 1 && <button className="btn btn-sm" onClick={() => commit(1)}>Reset</button>}
        <span className="muted small textzoom-hint">Pinch text to resize</span>
      </div>
      <div ref={boxRef} className="textzoom" style={{ fontSize: `${BASE_PX * scale}px` }} data-scale={scale}>
        {children}
      </div>
    </div>
  );
}
